import finlandPlaceIdsSeed from './data/finland-place-ids-seed.json';
import type { RemoteEligibility } from './location-resolver';

/**
 * TEC-482: every gazetteer placeId that is Finland itself, one of its 18 admin1 (maakunta)
 * regions, or one of its 910 seeded cities, i.e. every `regionTags` value that is exclusively
 * Finland. Sourced from data/finland-place-ids-seed.json, a small Finland-only extract of the
 * same checked-in GeoNames seed data the gazetteer and `region-tag-labels.json` are built from
 * (regenerate via `npx tsx scripts/generate-finland-place-ids-seed.ts`), so it can't drift out
 * of sync. Deliberately not the raw global admin1.json/countries.json themselves: this module is
 * reachable from client bundles (SearchContext, MapView, LocationHierarchyPicker), and those
 * files carry every other country's subdivisions too (~600KB of dead weight per page load).
 *
 * Used to extend the site's Finland-baseline `isFinland:true` filter (previously only aware of
 * the old flat `location` string field) so it also correctly backs off for an explicit non-Finland
 * `regionTags` selection (a continent, a recruiting region like "Nordics"/"EMEA", which include
 * countries other than Finland, or any other country). Picking a Finland city/admin1/the Finland
 * country id itself is still "Finland-only", so the baseline correctly stays on for those.
 */

/** Finland's own gazetteer placeId, from the generated seed rather than hand-typed, so it can't
 * drift from FINLAND_PLACE_IDS below or from the underlying GeoNames data. */
export const FINLAND_COUNTRY_PLACE_ID: string = finlandPlaceIdsSeed.countryPlaceId;

export const FINLAND_PLACE_IDS: ReadonlySet<string> = new Set<string>([
  finlandPlaceIdsSeed.countryPlaceId,
  ...finlandPlaceIdsSeed.admin1PlaceIds,
  ...finlandPlaceIdsSeed.cityPlaceIds,
]);

/** True when `value` is Finland's own placeId, or one of its admin1/city placeIds. */
export function isFinlandRegionTagValue(value: string): boolean {
  return FINLAND_PLACE_IDS.has(value);
}

/**
 * Region tags whose area includes Finland: the FI country code, the Europe continent and the
 * recruiting regions with FI as a member (scripts/data/recruiting-regions.json; a test keeps this
 * list in sync). Used only for remote-only searches, see has-explicit-non-finland-selection.ts.
 */
export const FINLAND_CONTAINING_REGION_TAGS: ReadonlySet<string> = new Set(['FI', 'EU', 'EMEA', 'EUUNION', 'NORDICS']);


/** ISO 3166-1 alpha-2 code for Finland, matching the format `remoteEligibleCountryCodes`
 * (computeRemoteEligibleCountryCodes) stores values in. Used by buildFinlandBaselineFacetFilter
 * below, both client- and server-side. */
export const FINLAND_ISO_COUNTRY_CODE = 'FI';

/**
 * TEC-482 follow-up: the default "you're browsing Finland" baseline facet filter, the same role
 * `isFinland:true` has always played, pushed onto every search that hasn't explicitly picked a
 * different location. Gated on `newLocationSystemEnabled`: with the flag on, it also OR-includes
 * jobs whose remote eligibility reaches Finland (`remoteEligibleCountryCodes`), not just
 * physically-Finland ones (`regionTags`), so a remote-EMEA-eligible role shows up in the default
 * view the same way a physical Helsinki job already does, dogfooded behind the flag before it
 * becomes everyone's actual default. Flag off: unchanged, still the plain `isFinland:true` field
 * check every existing user already sees.
 */
export function buildFinlandBaselineFacetFilter(newLocationSystemEnabled: boolean): string | string[] {
  if (!newLocationSystemEnabled) return 'isFinland:true';
  return [`regionTags:${FINLAND_COUNTRY_PLACE_ID}`, `remoteEligibleCountryCodes:${FINLAND_ISO_COUNTRY_CODE}`];
}

/**
 * Verified live against a real (unmigrated) Typesense instance: an OR-group referencing a facet
 * field the schema doesn't know about yet is a hard 400 for the whole query, not a graceful
 * zero-match side of the OR — `{"error":"Could not find a filter field named
 * \`remoteEligibleCountryCodes\` in the schema."}`. In the real deploy sequence this can't
 * actually happen (the schema migration applies as part of the same deploy that ships this
 * code, before any real visitor could ever flip the flag), but relying on that ordering alone
 * for something explicitly meant to be dogfooded pre-deploy is fragile. This is the retry-once
 * fallback: given a `facetFilters` array that may contain the compound baseline filter (as
 * returned by `buildFinlandBaselineFacetFilter(true)`), returns a copy with it swapped back to
 * the plain `isFinland:true` check, or the input unchanged if the compound filter isn't present.
 * Used by callers that catch a failed search using the compound filter and want to retry once
 * with the known-safe fallback rather than surface a broken search to the user.
 */
export function swapCompoundFinlandFilterForFallback(facetFilters: unknown): unknown {
  if (!Array.isArray(facetFilters)) return facetFilters;
  const compound = buildFinlandBaselineFacetFilter(true) as string[];
  const idx = facetFilters.findIndex(
    (entry) => Array.isArray(entry) && entry.length === compound.length && compound.every((v, i) => entry[i] === v),
  );
  if (idx === -1) return facetFilters;
  const next = [...facetFilters];
  next[idx] = 'isFinland:true';
  return next;
}

/**
 * TEC-482 Phase 8: the `regionTags`-based replacement for the old `checkIsFinland`/`isFinlandJob`
 * loose check (data-generation pipelines, not search filters — see `buildFinlandBaselineFacetFilter`
 * for the search-facet equivalent this mirrors). A remote-scoped job's `regionTags` never carries
 * Finland's own placeId (only a physically-Finland resolution adds it, see
 * `computeRegionTagsForResolutions` in `location-resolver.ts`), so this ORs in
 * `remoteEligibleCountryCodes` too: physically in Finland, or remotely eligible for someone in
 * Finland (covers "Remote - Finland", "Remote - Global", "Remote - Europe", "Remote - EMEA", bare
 * "Remote" — all of these reach Finland without ever tagging it specifically).
 */
export function isJobFinlandByRegionTags(job: {
  regionTags?: string[];
  remoteEligibleCountryCodes?: string[];
}): boolean {
  if (job.regionTags?.some(isFinlandRegionTagValue)) return true;
  return job.remoteEligibleCountryCodes?.includes(FINLAND_ISO_COUNTRY_CODE) ?? false;
}

/**
 * TEC-482 Phase 8: the `regionTags`-based replacement for the old `isFinlandJobStrict` — same
 * physical-Finland check as `isJobFinlandByRegionTags`, but the remote half only counts a job
 * explicitly scoped to Finland alone (`remoteEligibility.scopeType === 'country'`), not one
 * merely eligible for Finland among many other countries (global/continent/namedRegion scopes).
 * `remoteEligibleCountryCodes` can't make that distinction on its own — it's populated the same
 * way for "Remote - Finland" and "Remote - Global" alike — so this reads the structured scope
 * itself instead, matching what `isFinlandJobStrict` excluded ("Remote - Europe"/"Remote - Global").
 */
export function isJobFinlandByRegionTagsStrict(job: {
  regionTags?: string[];
  remoteEligibility?: RemoteEligibility;
}): boolean {
  if (job.regionTags?.some(isFinlandRegionTagValue)) return true;
  return job.remoteEligibility?.scopeType === 'country' && job.remoteEligibility.countryCode === FINLAND_ISO_COUNTRY_CODE;
}
